/**
 * Attribute Framing experiment translations
 * Last updated: 2025-08-18 14:25:55
 * Updated by: JulianPolly
 */

export const attributeFraming = {
    de: {
        title: "Wahl im Kühlregal – welcher Becher passt zu dir?", // This will be used for the menu
        description: "Erlebe, wie unterschiedliche Darstellungen derselben Information deine Entscheidungen beeinflussen können.",
        screens: {
            task: {
                title: "Wahl im Kühlregal – welcher Becher passt zu dir?", // This will be used for the experiment heading
                instructions: "Du bist im Supermarkt unterwegs und möchtest Joghurt kaufen. Im Kühlregal stehen viele verschiedene Sorten – du entscheidest dich für Naturjoghurt im Plastikbecher. Zwei Produkte fallen dir besonders ins Auge: Beide enthalten den gleichen Joghurt, kosten gleich viel und stammen vom selben Hersteller. Für welchen Becher entscheidest du dich?",
                question: "Für welchen Becher entscheidest du dich?",
                options: {
                    recycled: {
                        title: "Becher A",
                        description: "Dieser Becher besteht aus 35 % recyceltem Plastik."
                    },
                    new: {
                        title: "Becher B",
                        description: "Dieser Becher besteht aus 65 % Neuplastik."
                    }
                },
                selection: {
                    recycled: "Du hast Becher A ausgewählt.",
                    new: "Du hast Becher B ausgewählt."
                },
                reasonLabel: "Bitte begründen deine Auswahl:",
                reasonRequired: "(erforderlich)",
                reasonPlaceholder: "Bitte gib eine kurze Begründung an."
            },
            results: {
                title: "Deine Ergebnisse",
                content: "Ergebnisse deiner Klasse - Hier siehst du, wie deine Klasse abgestimmt hat.",
                choice: "Deine Wahl:",
                classResults: "Ergebnisse deiner Klasse",
                yourChoiceRecycled: "Du hast dich für den Becher aus 35 % recyceltem Plastik entschieden.",
                yourChoiceNew: "Du hast dich für den Becher aus 65 % Neuplastik entschieden.",
                noChoice: "Du hast keine Auswahl getroffen."
            },
            explanation: {
                title: "Was ist Attribut-Framing?",
                sections: {
                    whatIsIt: {
                        title: "Was ist Attribut-Framing?",
                        content: "Attribut-Framing ist ein psychologisches Phänomen, bei dem die Darstellung eines Attributs (einer Eigenschaft) die Entscheidungsfindung beeinflusst. Dieselbe Information kann positiv oder negativ dargestellt werden, was zu unterschiedlichen Bewertungen und Entscheidungen führt."
                    },
                    howItWorks: {
                        title: "Wie funktioniert es?",
                        content: "Beim Attribut-Framing wird eine bestimmte Eigenschaft eines Produkts oder einer Situation auf unterschiedliche Weise präsentiert. Ein Glas kann als 'halb voll' oder 'halb leer' beschrieben werden – es ist die gleiche Menge, aber die Formulierung erzeugt unterschiedliche Assoziationen und Gefühle."
                    },
                    experiment: {
                        title: "In unserem Experiment",
                        content: "In diesem Experiment hast du zwischen zwei identischen Joghurtbechern gewählt. Der erste wurde als '35% recyceltes Plastik enthaltend' beschrieben, der zweite als '65% Neuplastik enthaltend'. Mathematisch sind beide Beschreibungen identisch (35% recycelt + 65% neu = 100%), aber die unterschiedliche Rahmung kann deine Wahrnehmung und Entscheidung beeinflussen."
                    },
                    realLife: {
                        title: "Auswirkungen im Alltag",
                        content: "Attribut-Framing begegnet uns täglich: '90% fettfrei' klingt attraktiver als '10% Fett'; '95% Erfolgsrate' wirkt überzeugender als '5% Misserfolgsrate'. Werbung, Politik und Medizin nutzen diese Technik häufig, um Botschaften vorteilhaft zu präsentieren und unsere Entscheidungen zu beeinflussen."
                    }
                },
                sources: {
                    title: "Quellen",
                    items: [
                        "Levin, I. P., Schneider, S. L., & Gaeth, G. J. (1998). All frames are not created equal: A typology and critical analysis of framing effects. Organizational Behavior and Human Decision Processes, 76(2), 149-188.",
                        "Krishnamurthy, P., Carter, P., & Blair, E. (2001). Attribute framing and goal framing effects in health decisions. Organizational Behavior and Human Decision Processes, 85(2), 382-399.",
                        "Gong, J., Zhang, Y., Yang, Z., & Huang, Y. (2013). The framing effect in medical decision-making: A review of the literature. Psychology, Health & Medicine, 18(6), 645-653."
                    ]
                }
            }
        }
    },
    en: {
        title: "Choice in the Refrigerated Section – Which Cup Suits You?", // This will be used for the menu
        description: "Experience how different presentations of the same information can influence your decisions.",
        screens: {
            task: {
                title: "Choice in the Refrigerated Section – Which Cup Suits You?", // This will be used for the experiment heading
                instructions: "You're at the supermarket and want to buy yogurt. In the refrigerated section, there are many different varieties – you decide on plain yogurt in a plastic cup. Two products catch your eye: Both contain the same yogurt, cost the same, and are from the same manufacturer. Which cup do you choose?",
                question: "Which cup do you choose?",
                options: {
                    recycled: {
                        title: "Cup A",
                        description: "This cup is made of 35% recycled plastic."
                    },
                    new: {
                        title: "Cup B",
                        description: "This cup is made of 65% new plastic."
                    }
                },
                selection: {
                    recycled: "You selected Cup A.",
                    new: "You selected Cup B."
                },
                reasonLabel: "Please explain your choice:",
                reasonRequired: "(required)",
                reasonPlaceholder: "Please enter a brief explanation."
            },
            results: {
                title: "Your Results",
                content: "Class results - Here you can see how your class voted.",
                choice: "Your choice:",
                classResults: "Your class results",
                yourChoiceRecycled: "You chose the cup made of 35% recycled plastic.",
                yourChoiceNew: "You chose the cup made of 65% new plastic.",
                noChoice: "You did not make a selection."
            },
            explanation: {
                title: "What is Attribute Framing?",
                sections: {
                    whatIsIt: {
                        title: "What is Attribute Framing?",
                        content: "Attribute framing is a psychological phenomenon where the presentation of an attribute (a property) influences decision-making. The same information can be presented positively or negatively, leading to different evaluations and decisions."
                    },
                    howItWorks: {
                        title: "How Does It Work?",
                        content: "In attribute framing, a specific property of a product or situation is presented in different ways. A glass of water can be described as 'half full' or 'half empty' – it's the same amount, but the wording creates different associations and feelings."
                    },
                    experiment: {
                        title: "In Our Experiment",
                        content: "In this experiment, you chose between two identical yogurt cups. The first was described as 'containing 35% recycled plastic', the second as 'containing 65% new plastic'. Mathematically, both descriptions are identical (35% recycled + 65% new = 100%), but the different framing can influence your perception and decision."
                    },
                    realLife: {
                        title: "Everyday Impact",
                        content: "Attribute framing appears in everyday life: '90% fat-free' sounds more attractive than '10% fat'; '95% success rate' seems more convincing than '5% failure rate'. Advertising, politics, and medicine often use this technique to present messages advantageously and influence our decisions."
                    }
                },
                sources: {
                    title: "Sources",
                    items: [
                        "Levin, I. P., Schneider, S. L., & Gaeth, G. J. (1998). All frames are not created equal: A typology and critical analysis of framing effects. Organizational Behavior and Human Decision Processes, 76(2), 149-188.",
                        "Krishnamurthy, P., Carter, P., & Blair, E. (2001). Attribute framing and goal framing effects in health decisions. Organizational Behavior and Human Decision Processes, 85(2), 382-399.",
                        "Gong, J., Zhang, Y., Yang, Z., & Huang, Y. (2013). The framing effect in medical decision-making: A review of the literature. Psychology, Health & Medicine, 18(6), 645-653."
                    ]
                }
            }
        }
    }
};